import { Component, EventEmitter, inject, Input, OnInit, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ApeDrawerComponent } from "../ape-drawer/ape-drawer.component";
import { ApeNotificationItemComponent } from "../ape-notification-item/ape-notification-item.component";
import { ApeButtonComponent } from "../ape-button/ape-button.component";
import { TranslatePipe } from "../../pipe/translation.filter.pipe";
import { TRANSLATION_KEYS } from "../../constants/translations/translation-keys";
import { LoggerService } from "../../../ancestors/logger.service";
import { NotificationStateService } from "../../services/state/notification-state.service";
import { AppNotification } from "alloy-performance-engine-cl";
import { UserDataService } from "src/app/services/data/user-data.service";
import { ApeSimpleSelectComponent, SimpleSelectChangeEvent, SimpleSelectOption } from "../ape-simple-select/ape-simple-select.component";
import { TranslateService } from "src/app/util/translate.service";

enum NotificationFilter {
  ALL = "all",
  UNREAD = "unread",
  READ = "read"
}

@Component({
  selector: "ape-notification-sidenav",
  standalone: true,
  imports: [
    CommonModule,
    ApeDrawerComponent,
    ApeNotificationItemComponent,
    ApeButtonComponent,
    TranslatePipe,
    ApeSimpleSelectComponent
  ],
  templateUrl: "./ape-notification-sidenav.component.html"
})
export class ApeNotificationSidenavComponent implements OnInit {
  readonly TRANSLATION_KEYS = TRANSLATION_KEYS;

  private logger = inject(LoggerService);

  private notificationState = inject(NotificationStateService);

  private translate = inject(TranslateService);

  private userData = inject(UserDataService);

  /**
   * Visibilità del drawer
   */
  @Input() visible = false;

  /**
   * Evento di cambio visibilità
   */
  @Output() visibleChange = new EventEmitter<boolean>();

  /**
   * Observable per la lista notifiche
   */
  notifications$ = this.notificationState.notifications$;

  /**
   * Observable per il conteggio non lette
   */
  unreadCount$ = this.notificationState.unreadCount$;

  /**
   * Observable per lo stato di caricamento
   */
  loading$ = this.notificationState.loading$;

  /**
   * Opzioni per la select
   */
  options: SimpleSelectOption[] = [
    {
      key: NotificationFilter.ALL,
      value: this.translate.instant(this.TRANSLATION_KEYS.notifications.ALL_NOTIFICATIONS)
    },
    {
      key: NotificationFilter.UNREAD,
      value: this.translate.instant(this.TRANSLATION_KEYS.notifications.ALL_NOT_READ)
    },
    {
      key: NotificationFilter.READ,
      value: this.translate.instant(this.TRANSLATION_KEYS.notifications.ALL_READ)
    }
  ];

  /**
   * Opzione selezionata
   */
  protected selectedFilter: SimpleSelectOption = this.options[0];

  constructor() {
    this.logger.setCaller(this.constructor.name);
  }

  ngOnInit(): void {
    // Carica le notifiche quando il componente viene inizializzato, se l'utente è loggato
    try {
      const userId = this.userData.getUserId();
      if (userId) {
        void this.notificationState.loadNotifications();
      }
    } catch (_) {
      // utente non loggato, non faccio nulla
    }
  }

  /**
   * Gestisce il click su una notifica
   */
  onNotificationClick(notification: AppNotification): void {
    this.logger.info(`Notification clicked: ${notification.notificationId}`);
    this.notificationState.navigateToNotification(notification);
    this.close();
  }

  /**
   * Marca tutte le notifiche come lette
   */
  onMarkAllAsRead(): void {
    this.logger.info("Mark all as read clicked");
    void this.notificationState.markAllAsRead();
  }

  /**
   * Gestisce il cambio di visibilità del drawer
   */
  onVisibleChange(visible: boolean): void {
    this.visible = visible;
    this.visibleChange.emit(visible);

    // Se il drawer viene aperto, ricarica le notifiche
    if (visible) {
      this.loadNotifications(this.selectedFilter?.key as NotificationFilter);
    }
  }

  /**
   * Chiude il drawer
   */
  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  /**
   * Gestisce il cambio di selezione della select
   */
  onSelectionChange(event: SimpleSelectChangeEvent): void {
    this.logger.info(`Selection changed: ${event.selectedOption.key}`);
    switch (event.selectedOption.key as NotificationFilter) {
      case NotificationFilter.UNREAD:
        this.loadNotifications(NotificationFilter.UNREAD);
        break;
      case NotificationFilter.READ:
        this.loadNotifications(NotificationFilter.READ);
        break;
      default:
        this.loadNotifications();
        break;
    }
  }

  private loadNotifications(filter?: NotificationFilter): void {
    this.logger.info(`Loading notifications with filter: ${filter}`);
    if (!filter) {
      this.selectedFilter = this.options[0];
      void this.notificationState.loadNotifications();
      return;
    }
    switch (filter) {
      case NotificationFilter.UNREAD:
        this.selectedFilter = this.options[1];
        void this.notificationState.loadNotifications({ unreadOnly: true });
        break;
      case NotificationFilter.READ:
        this.selectedFilter = this.options[2];
        void this.notificationState.loadNotifications({ readOnly: true });
        break;
      default:
        this.selectedFilter = this.options[0];
        void this.notificationState.loadNotifications();
        break;
    }
  }
}
